import type { ApprovalRequestInput } from '../projects/components/ApprovalRequestBlock.display';

import type { MailComposeValues } from './mail-compose-approval-layout';
import { formatAddressList, parseAddressListInput } from './nango-connector-approval';

export const EMAIL_SESSION_CONTENT_TOOL_NAME = 'email_session_content';

export const isSessionEmailApprovalTool = (toolName: string): boolean =>
  toolName === EMAIL_SESSION_CONTENT_TOOL_NAME;

/**
 * Server-authored preview (`context.approval_preview`) the backend attaches
 * before the card opens: the subject and body that send when the clinician
 * leaves them untouched.
 */
export interface SessionEmailApprovalPreview {
  subject: string;
  /** `null` when the stored body is a PDF form or too large to preview. */
  content: string | null;
  hasAttachment: boolean;
}

export interface SessionEmailApprovalFields {
  kind: 'consult_note' | 'document';
  preview: SessionEmailApprovalPreview | null;
  initial: MailComposeValues;
}

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const readPreview = (
  context: Record<string, unknown> | undefined
): SessionEmailApprovalPreview | null => {
  const raw = context?.approval_preview;
  if (!raw || typeof raw !== 'object') return null;
  const { subject, content, has_attachment } = raw as Record<string, unknown>;
  if (typeof subject !== 'string') return null;
  return {
    subject,
    content: typeof content === 'string' ? content : null,
    hasAttachment: has_attachment === true,
  };
};

export const readSessionEmailApprovalFields = (
  approval: ApprovalRequestInput
): SessionEmailApprovalFields => {
  const input = approval.tool_input ?? {};
  const preview = readPreview(approval.context);
  return {
    kind: input.kind === 'document' ? 'document' : 'consult_note',
    preview,
    initial: {
      to: formatAddressList(input.to_emails),
      cc: formatAddressList(input.cc_emails),
      bcc: formatAddressList(input.bcc_emails),
      subject: asString(input.subject) || (preview?.subject ?? ''),
      body: asString(input.content) || (preview?.content ?? ''),
    },
  };
};

/**
 * The approved tool input. Subject and body the clinician left at the
 * server's defaults are omitted so the stored note (and per-recipient
 * templated send) is used as-is.
 */
export const buildSessionEmailModifiedInput = (
  original: Record<string, unknown>,
  fields: SessionEmailApprovalFields,
  values: MailComposeValues
): Record<string, unknown> => {
  const next: Record<string, unknown> = {
    ...original,
    to_emails: parseAddressListInput(values.to),
  };
  for (const key of ['cc_emails', 'bcc_emails', 'subject', 'content']) delete next[key];
  const cc = parseAddressListInput(values.cc);
  const bcc = parseAddressListInput(values.bcc);
  if (cc.length > 0) next.cc_emails = cc;
  if (bcc.length > 0) next.bcc_emails = bcc;

  const subject = values.subject.trim();
  if (subject && subject !== fields.preview?.subject.trim()) next.subject = subject;

  const body = values.body;
  const hadOverride = asString(original.content).length > 0;
  if (body.trim() && (hadOverride || body !== (fields.preview?.content ?? ''))) {
    next.content = body;
  }
  return next;
};
